/**
 * AppStoreStatsCard — Store-Zugaenge (Apple App Store Connect + Google Play)
 * pro Firma, fuer App-Download-Zahlen im Sonntags-Report.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Smartphone, Save, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { apple_key_id: "", apple_issuer_id: "", apple_vendor_number: "", apple_app_sku: "", apple_private_key: "", play_bucket: "", play_package: "", play_sa_json: "" };

export default function AppStoreStatsCard({ companyId }) {
  const [cfg, setCfg] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!companyId || companyId === "all") return;
    setResult(null);
    axios.get(`${API}/app-stats/${companyId}/settings`, { withCredentials: true })
      .then((r) => setCfg({ ...EMPTY, ...r.data }))
      .catch(() => setCfg(EMPTY));
  }, [companyId]);

  if (!companyId || companyId === "all") return null;
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/app-stats/${companyId}/settings`, cfg, { withCredentials: true });
      toast.success("Store-Zugänge gespeichert (verschlüsselt)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/app-stats/${companyId}/test`, {}, { withCredentials: true });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test fehlgeschlagen"); }
    setTesting(false);
  };

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="app-store-stats-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Smartphone className="w-4 h-4" /> App-Store-Downloads (für den Sonntags-Report)
        </CardTitle>
        <p className="text-xs text-[var(--studio-text-muted)]">
          Verbinde App Store Connect und/oder Google Play — die Download-Zahlen der App erscheinen dann automatisch im Wochen-Report. Schlüssel werden verschlüsselt gespeichert.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold">🍎 Apple App Store Connect <span className="font-normal text-[var(--studio-text-muted)]">(Benutzer &amp; Zugriff → Integrationen → API-Schlüssel erstellen)</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label className="text-[11px]">Key ID</Label><Input className="h-8 text-sm" value={cfg.apple_key_id} onChange={set("apple_key_id")} data-testid="apple-key-id-input" /></div>
            <div><Label className="text-[11px]">Issuer ID</Label><Input className="h-8 text-sm" value={cfg.apple_issuer_id} onChange={set("apple_issuer_id")} data-testid="apple-issuer-id-input" /></div>
            <div><Label className="text-[11px]">Vendor-Nummer (unter „Zahlungen und Finanzberichte")</Label><Input className="h-8 text-sm" value={cfg.apple_vendor_number} onChange={set("apple_vendor_number")} data-testid="apple-vendor-input" /></div>
            <div><Label className="text-[11px]">App-SKU (optional — leer = alle Apps des Kontos)</Label><Input className="h-8 text-sm" value={cfg.apple_app_sku} onChange={set("apple_app_sku")} data-testid="apple-sku-input" /></div>
          </div>
          <div><Label className="text-[11px]">Privater Schlüssel (.p8-Datei-Inhalt)</Label>
            <Textarea className="text-xs font-mono" rows={3} placeholder="-----BEGIN PRIVATE KEY-----" value={cfg.apple_private_key} onChange={set("apple_private_key")} data-testid="apple-p8-input" /></div>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold">🤖 Google Play <span className="font-normal text-[var(--studio-text-muted)]">(Play Console → Berichte herunterladen → Cloud-Storage-URI)</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label className="text-[11px]">Cloud-Storage-Bucket (gs://pubsite_prod_rev_…)</Label><Input className="h-8 text-sm" value={cfg.play_bucket} onChange={set("play_bucket")} data-testid="play-bucket-input" /></div>
            <div><Label className="text-[11px]">Package-Name (z.B. ch.strongfit.app)</Label><Input className="h-8 text-sm" value={cfg.play_package} onChange={set("play_package")} data-testid="play-package-input" /></div>
          </div>
          <div><Label className="text-[11px]">Service-Account-JSON (mit Leserecht auf Play-Berichte)</Label>
            <Textarea className="text-xs font-mono" rows={3} placeholder='{"type":"service_account", ...}' value={cfg.play_sa_json} onChange={set("play_sa_json")} data-testid="play-sa-input" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="h-8 gap-1" onClick={save} disabled={saving} data-testid="app-store-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1" onClick={test} disabled={testing} data-testid="app-store-test-btn">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Verbindung testen (Downloads 7 Tage)
          </Button>
        </div>
        {result && (
          <div className="text-sm space-y-1" data-testid="app-store-test-result">
            {result.apple !== null && <p>🍎 App Store: <b>{result.apple}</b> Downloads (7 Tage)</p>}
            {result.play !== null && <p>🤖 Google Play: <b>{result.play}</b> Downloads (7 Tage)</p>}
            {(result.errors || []).map((e, i) => <p key={i} className="text-red-600 text-xs">{e}</p>)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
